import type { PermissionGates } from './permission-gates';
import type { Permission } from './permissions.catalog';

/**
 * What the UI does about a permission the current user does not hold.
 *
 * The rights in a session are already the answer to "may I": the People service filters
 * them by the seat the person sits in, so a right the plan does not pay for never arrives.
 * That leaves the frontend one question — whether to say so.
 *
 * - `open`   — the right is held; render the real thing.
 * - `locked` — withheld, and the person looking is the one who could buy it back. Render a
 *              placeholder shaped like the real section, name the reason, offer the upgrade.
 * - `hidden` — withheld, and naming it would help nobody. Render nothing, as before.
 *
 * Only the owner of the selected vessel ever sees `locked`. Crew cannot move the plan, so
 * for them a locked card is an advert for something they cannot act on; the feature simply
 * is not there. The owner is also the only one for whom "you don't have this right" reliably
 * means "your plan does not carry it" — everyone else may simply not have been granted it.
 */
export type AccessVerdict = 'open' | 'locked' | 'hidden';

export interface PlanAccessInput {
  /** Every permission the surface needs; it is open only if all of them are held. */
  permissions: readonly Permission[];
  can: (permission: Permission) => boolean;
  /** True only when the active account is a vessel this user owns. */
  isVesselOwner: boolean;
  /** Built against the seat the vessel's plan buys — see `buildPermissionGates`. */
  gates: PermissionGates;
}

/**
 * Whether a withheld permission is one a bigger plan would restore.
 *
 * Both halves matter. `reasonFor === 'plan'` rules out the seat gate, which an owner fixes
 * by moving somebody between seats rather than by paying more. `unlockableByUpgrade` rules
 * out the permissions that sit in no seat allow-list at all — `Notifications`, `RoomsBasic`
 * and the like — where an upgrade would change nothing and offering one would be a lie.
 */
const upgradeWouldGrant = (name: Permission, gates: PermissionGates): boolean =>
  gates.reasonFor(name) === 'plan' && gates.unlockableByUpgrade(name);

/**
 * The verdict for one surface.
 *
 * A surface guarded by several permissions is locked only if the plan is what is missing
 * across the board. If even one of them is withheld for some other reason — not in any seat
 * list, or gated by seat rather than tier — then upgrading would not open the surface, and
 * promising otherwise is worse than staying quiet.
 *
 * `gates.active` is false until `GET /permissions/seats` answers. Until then nothing can be
 * attributed to the plan, so every withheld permission reads as `hidden`: the pre-existing
 * behaviour, and the one that cannot mislead.
 */
export const resolveAccess = ({
  permissions,
  can,
  isVesselOwner,
  gates,
}: PlanAccessInput): AccessVerdict => {
  const missing = permissions.filter((name) => !can(name));
  if (missing.length === 0) return 'open';
  if (!isVesselOwner || !gates.active) return 'hidden';
  return missing.every((name) => upgradeWouldGrant(name, gates)) ? 'locked' : 'hidden';
};

/**
 * The verdict for a surface that opens on any *one* of several permissions.
 *
 * `resolveAccess` is all-of: it answers for a section that needs every permission it names.
 * The services are frequently the other way round — `GET /transaction` clears on
 * `TransactionsBasic` *or* `Transactions`, and a navigate plan carries the second without
 * the first — so asking the all-of question there would padlock a section the plan opens.
 *
 * One granted right opens it; failing that, one right an upgrade would restore is enough to
 * make the placeholder honest.
 */
export const resolveAnyAccess = (verdicts: readonly AccessVerdict[]): AccessVerdict => {
  if (verdicts.includes('open')) return 'open';
  return verdicts.includes('locked') ? 'locked' : 'hidden';
};
